// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getSettingsCollection } from "@tps/App/Store/Selectors/SettingsSelectors";
import breakpoints from "@tps/XDL/PreStyle/breakpoints";
import systemZIndex from "@tps/XDL/PreStyle/zIndex";
import isDate from "lodash/isDate";

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * split without empty or undefined values
 * @param value
 */
export const flatSplit = (value?: string, target = "") => (value || "").split(target).filter(item => item) || [];

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => {};

/**
 * Helper function to export data to csv file
 * @param fields
 * @param data
 * @param filename
 */
export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const formatDate = (date: Date | null) => {
    if (!date) {
        return "";
    }

    let dt = date;
    if (!isDate(dt)) {
        dt = new Date(dt);
    }

    return getLocalizedDateTime({
        dateTime: dt,
        options: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        },
    });
};

export const xGetFulfillmentLabel = (state: ApplicationState) => {
    const { fulfillmentMethod, isAuthenticated, rememberMe, isGuest, billToId, pickUpWarehouse } = getSession(state);
    const billToState = getBillToState(state, billToId);
    const { value: billTo } = billToState;
    let addressLabel = "";
    if (fulfillmentMethod === FulfillmentMethod.Ship && (isAuthenticated || rememberMe) && !isGuest && billTo) {
        addressLabel = `${billTo.companyName}, ${billTo.fullAddress}`;
    } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
        addressLabel = pickUpWarehouse ? `${pickUpWarehouse.description || pickUpWarehouse.name}` : "";
    }

    return addressLabel;
};

/**
 * 
 * @param options 
 * optionsExample = {
    when window width is >= 0px
    0: 2,
    // when window width is >= 480px
    480: 4,
    // when window width is >= 1024px
    1024: 6,
    };
 * @returns 
 */
export const generateSlidesPerViewMap = (options?: { [key: number]: number }) => {
    const { maxWidths } = breakpoints;
    const values: any = {};
    if (!options) {
        values[maxWidths[0]] = {
            slidesPerView: 4,
        };
        values[maxWidths[1]] = {
            slidesPerView: 4,
        };
        values[maxWidths[2]] = {
            slidesPerView: 6,
        };
        values[maxWidths[3]] = {
            slidesPerView: 6,
        };
        return values;
    }

    Object.keys(options).forEach(key => {
        if (!maxWidths[+key]) {
            return;
        }
        values[maxWidths[+key]] = {
            slidesPerView: +options[+key],
        };
    });
    return values;
};

export function XngConvertDateOnlyStringToDate(dateString: string | undefined) {
    return dateString ? new Date(new Date(dateString).getTime()) : undefined;
}

export const shouldSkipRealTimeCall = (props: any) => {
    const { hideItemsOutOfStock } = getSettingsCollection(props.getState()).catalogCustomSettings;
    const isParentSpecialOrder = props.product?.detail?.isSpecialOrder;
    const areAllVariantsSpecialOrder =
        props.variantChildren?.length > 0 &&
        props.variantChildren.every((product: any) => product.detail?.isSpecialOrder);
    return isParentSpecialOrder || (areAllVariantsSpecialOrder && !hideItemsOutOfStock);
};
